// 출퇴근 위치 확인 (2026-10-02, spec docs/superpowers/specs/2026-10-02-attendance-location-design.md §5).
//
// 출퇴근 화면에 #attLocStrip 이 있을 때만 돈다 — 서버가 그것을 그리는 것은 이 요청이 회사 IP 로 통과하지 못하고,
// 승인된 재택 · 외근 · 출장일도 아니고, 위치로 받아 줄 근무지가 있을 때뿐이다(AttendanceGeo).
//
//   1. 권한을 본다: 허용이면 조용히 위치를 잡아 미리 확인하고, 아직 묻지 않았으면 「위치 확인」 한 줄을, 거부면
//      「켜는 방법」을 띄운다. 브라우저 창은 언제나 사람이 누른 뒤에만 뜬다(설명 → 「계속」 → 창).
//   2. 위치는 watchPosition 으로 10초까지 받되, 잡히는 대로 판정한다(locateRun): 첫 위치를 곧바로 묻고, 통과 ·
//      근무지 밖처럼 더 기다려도 바뀌지 않는 답이면 그 자리에서 끝낸다. 오차가 큰 위치만 1.5초 더 기다렸다
//      보이고(대략적인 위치는 곧바로), 그 뒤에도 창이 닫힐 때까지 더 정확한 위치가 오면 조용히 다시 판정해
//      바꾼다. 「위치를 사용할 수 없음」 오류는 1.5초 안에 위치가 오지 않으면 끝이다.
//   3. POST /attendance/locate 가 출퇴근 POST 와 같은 판정을 돌려준다(저장 없음) — 띠에 결과, 막히면 시트에 이유와 길.
//      방금 실패한 상태에서 출퇴근 단추를 누르면 그 설명 시트를 곧바로 열고 새로 확인은 뒤에서 돈다.
//   4. 출퇴근 단추는 새로 잡은 위치(1분 안)를 lat · lng · accuracy 로 실어 보낸다 — 서버가 거리를 다시 잰다.
//   5. 권한이 바뀌거나(설정에서 켜고 돌아옴) 화면으로 돌아오면 다시 본다.
//   6. 거부된 권한은 웹 API 로 다시 물을 수 없다. 크롬 144+ 는 브라우저가 그리는 <geolocation> 버튼이 있어(거부했어도
//      누르면 다시 묻는다) 거부 시트의 주 버튼이 된다 — 그 밖의 브라우저는 기기별 설정 안내만.
//   7. 「정확한 위치」는 사이트가 켤 수 없다. 오차 1km 이상(대략적인 위치)은 서버가 거절하고 시트가 켜는 곳을 보인다.

import {
    locHelpCase, betterFix, fresh, errorState, verdictState, sheetModeFor, clockAllowed,
    formatDistance, fill, stripText, stripAction,
    verdictFinal, worthJudging, settleDelay, pressOpensSheet, UNAVAILABLE_GRACE_MS, SLOW_HINT_MS,
} from './attendance-geo-logic.js';
import { openSheet, closeSheet } from './mobile-shell.js';
import { loadLeaflet, pinIcon, osmLayer } from './geo-map.js';
import { showToast } from './calendar-history.js';

const WATCH_MS = 10000;
const FIX_MAX_AGE = 60000;

function readJSON(id) {
    try { return JSON.parse(document.getElementById(id)?.textContent || '{}'); } catch { return {}; }
}

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

function standaloneNow() {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true;
}

function helpCase() {
    return locHelpCase({
        ua: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        standalone: standaloneNow(),
        mobile: !!(navigator.userAgentData && navigator.userAgentData.mobile),
    });
}

// permissionState answers 'granted' | 'prompt' | 'denied' | 'unknown' (no Permissions API).
async function permissionState() {
    if (!navigator.permissions || !navigator.permissions.query) return 'unknown';
    try {
        const st = await navigator.permissions.query({ name: 'geolocation' });
        return st.state || 'unknown';
    } catch {
        return 'unknown';
    }
}

async function locate(fix) {
    const body = new URLSearchParams({ lat: String(fix.lat), lng: String(fix.lng), accuracy: String(fix.accuracy) });
    const res = await fetch('/attendance/locate', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-CSRF-Token': csrfToken(), 'X-Requested-With': 'fetch', Accept: 'application/json' },
        body,
    });
    if (!res.ok) throw new Error('locate ' + res.status);
    return res.json();
}

// locateRun is one look: it watches the position for up to WATCH_MS and asks the server about each notably
// sharper fix as it comes, one request at a time. report(v, later) gets verdicts {state, fix, resp} and
// failures {state} ('blocked' · 'unavailable' · 'timeout' · 'failed'): the first one as soon as it may show
// (settleDelay — at once when more waiting cannot change it), then, after a rough one and while the window is
// still open, each sharper verdict with later = true. A failure after a verdict showed changes nothing.
// Returns stop(), after which nothing is reported.
function locateRun(report) {
    const started = Date.now();
    let id = null;
    let cancelled = false;
    let watching = true;
    let shown = false;
    let inflight = false;
    let best = null;     // the sharpest fix seen
    let judged = null;   // the fix last sent to the server
    let waiting = null;  // a rough verdict waiting to settle
    let errCode = 0;
    let settleTimer = 0;
    let graceTimer = 0;
    let windowTimer = 0;
    const endWatch = () => {
        if (!watching) return;
        watching = false;
        clearTimeout(windowTimer);
        clearTimeout(graceTimer);
        if (id !== null) { try { navigator.geolocation.clearWatch(id); } catch { /* gone */ } }
    };
    const show = (v) => {
        if (cancelled) return;
        clearTimeout(settleTimer);
        settleTimer = 0;
        waiting = null;
        const later = shown;
        shown = true;
        report(v, later);
    };
    const fail = (state) => {
        endWatch();
        if (!shown && !inflight) show({ state });
    };
    const next = () => {
        if (watching && !inflight && best && worthJudging(judged, best)) judge(best);
    };
    const onVerdict = (v) => {
        if (cancelled) return;
        if (v.state === 'failed') {
            // The server could not be asked: before anything showed that is the answer; after, the shown verdict stands.
            if (!shown) { endWatch(); show(v); } else next();
            return;
        }
        if (verdictFinal(v.state)) { endWatch(); show(v); return; }
        if (shown) { show(v); next(); return; }
        waiting = v;
        const wait = watching ? settleDelay(v.state, Date.now() - started) : 0;
        if (wait <= 0) show(v);
        else if (!settleTimer) settleTimer = setTimeout(() => { settleTimer = 0; if (waiting) show(waiting); }, wait);
        next();
    };
    const judge = (f) => {
        inflight = true;
        judged = f;
        locate(f).then((resp) => ({ state: verdictState(resp), fix: f, resp }), () => ({ state: 'failed', fix: f }))
            .then((v) => { inflight = false; onVerdict(v); });
    };
    windowTimer = setTimeout(() => {
        endWatch();
        if (cancelled) return;
        if (waiting) show(waiting);
        else if (!best && !shown) show({ state: errorState(errCode === 2 ? 2 : 3) });
        // else a request is in flight: its verdict shows when it comes
    }, WATCH_MS);
    try {
        id = navigator.geolocation.watchPosition((pos) => {
            if (!watching) return;
            best = betterFix(best, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.max(1, pos.coords.accuracy || 1), at: Date.now() });
            clearTimeout(graceTimer);
            graceTimer = 0;
            next();
        }, (err) => {
            if (!watching) return;
            errCode = err ? err.code : 2;
            if (errCode === 1) { fail(errorState(1)); return; }
            if (best) return; // a fix is in hand: a passing error changes nothing
            if (errCode === 3) { fail(errorState(3)); return; }
            if (!graceTimer) graceTimer = setTimeout(() => { graceTimer = 0; if (!best) fail(errorState(2)); }, UNAVAILABLE_GRACE_MS);
        }, { enableHighAccuracy: true, maximumAge: 0, timeout: WATCH_MS });
    } catch {
        fail(errorState(2));
    }
    return () => {
        cancelled = true;
        clearTimeout(settleTimer);
        endWatch();
    };
}

export function setupAttendanceGeo() {
    const strip = document.getElementById('attLocStrip');
    if (!strip) return null;
    const L10N = readJSON('attLocLabels');
    const sheet = document.getElementById('attLocSheet');
    const text = strip.querySelector('[data-loc-text]');
    const stripBtn = strip.querySelector('[data-loc-strip-btn]');
    const forms = [...document.querySelectorAll('.attendance-clock-form[data-geo="1"]')];
    const kase = helpCase();
    // Chrome 144+'s <geolocation> element re-asks even after a denial — the one way back the page can offer.
    const geoEl = sheet?.querySelector('[data-loc-geo]');
    const regrant = !!geoEl && 'HTMLGeolocationElement' in window;
    if (sheet) {
        sheet.querySelectorAll('[data-loc-geo-has]').forEach((el) => { el.hidden = !regrant; });
        sheet.querySelectorAll('[data-loc-geo-not]').forEach((el) => { el.hidden = regrant; });
    }
    const stillBlockedText = () => (regrant && L10N.stillBlockedRegrant) || L10N.stillBlocked || '';

    // The old GPS-evidence hint speaks about a different setting; this strip replaces it here.
    const oldHint = document.getElementById('attendance-gps-hint');
    if (oldHint) oldHint.style.display = 'none';

    let state = 'need';
    let info = {};          // last locate answer, flattened: name, distance, accuracy, method, request, radius, wlat, wlng
    let fix = null;         // last fix that was judged
    let radius = 0;         // the nearest workplace's radius once known
    let run = null;         // the current look (one at a time): {settled, opts, promise, resolve, stop}
    let verdictAt = 0;      // when the last look settled
    let slow = false;       // the current look has waited SLOW_HINT_MS with no fix
    let pendingForm = null; // a clock button waiting for the check
    let perm = 'unknown';
    const checking = () => !!run && !run.settled;

    // ── strip ──
    const paint = () => {
        strip.dataset.state = state;
        text.textContent = (state === 'checking' && slow && L10N.slow) ? L10N.slow : (stripText(state, info, L10N) || text.textContent);
        const act = stripAction(state);
        const label = { check: L10N.btnCheck, recheck: L10N.btnRecheck, howto: (regrant && L10N.btnRegrant) || L10N.btnHowto, help: L10N.btnHelp }[act];
        stripBtn.hidden = !act || !label;
        stripBtn.dataset.act = act || '';
        stripBtn.textContent = label || '';
        if (state === 'checking') { stripBtn.hidden = true; }
        strip.dataset.ready = clockAllowed(state) ? '1' : '0';
    };

    // ── sheet ──
    const sheetStatus = sheet?.querySelector('[data-loc-status]');
    const sayInSheet = (t, kind) => {
        if (!sheetStatus) return;
        sheetStatus.textContent = t || '';
        sheetStatus.dataset.kind = kind || '';
    };
    let sheetMap = null;
    let sheetMapKey = '';
    const showMode = (mode, detail) => {
        if (!sheet) return;
        sheet.dataset.mode = mode;
        sheet.setAttribute('aria-labelledby', 'attLocTitle-' + mode);
        sheet.querySelectorAll('[data-loc-mode]').forEach((sec) => { sec.hidden = sec.dataset.locMode !== mode; });
        sheet.querySelectorAll('[data-loc-case]').forEach((el) => { el.hidden = !el.dataset.locCase.split(/\s+/).includes(kase); });
        sheet.querySelectorAll('[data-loc-detail]').forEach((el) => {
            const sec = el.closest('[data-loc-mode]');
            if (!sec || sec.dataset.locMode !== mode) return;
            const first = sec.querySelector('[data-loc-detail]');
            el.hidden = detail ? el.dataset.locDetail !== detail : el !== first;
        });
        const fb = sheet.querySelector('[data-loc-fallback]');
        if (fb) fb.hidden = mode === 'ask';
        const acts = { ask: ['later', 'ask'], blocked: ['close', 'recheck'], unavailable: ['close', 'recheck'], inaccurate: ['close', 'recheck'], approximate: ['close', 'recheck'], outside: ['close', 'recheck'], unsupported: ['close'] }[mode] || ['close'];
        sheet.querySelectorAll('[data-loc-act]').forEach((b) => { b.hidden = !acts.includes(b.dataset.locAct); });
        const radiusTxt = formatDistance(info.radius || radius, L10N.dist);
        sheet.querySelectorAll('[data-loc-fill]').forEach((el) => {
            const tpl = L10N[el.dataset.locFill];
            el.textContent = fill(tpl, {
                acc: formatDistance(info.accuracy, L10N.dist), radius: radiusTxt,
                name: info.name || '', dist: formatDistance(info.distance, L10N.dist),
            });
        });
        if (mode === 'outside' || mode === 'inaccurate') drawSheetMap(sheet.querySelector(`[data-loc-mode="${mode}"] [data-loc-map]`));
    };
    const openMode = (mode, detail, opener) => {
        if (!sheet) return;
        sayInSheet('');
        showMode(mode, detail);
        if (sheet.hidden) openSheet(sheet, { opener: opener || stripBtn, onClose: () => { pendingForm = null; } });
    };
    const closeIfOpen = () => { if (sheet && !sheet.hidden) closeSheet(sheet, true); };

    // The sheet's small map: where you are and the nearest workplace's circle.
    function drawSheetMap(el) {
        if (!el || !fix || info.wlat == null) { if (el) el.hidden = true; return; }
        el.hidden = false;
        // A fresh look that lands where the open sheet already shows keeps the map as it is.
        const key = [fix.lat, fix.lng, fix.accuracy, info.wlat, info.wlng, info.radius || radius].join();
        if (sheetMap && sheetMapKey === key && el.contains(sheetMap.getContainer())) return;
        sheetMapKey = key;
        loadLeaflet().then((L) => {
            if (sheetMap) { sheetMap.remove(); sheetMap = null; }
            if (!el.isConnected || el.hidden) return;
            const map = L.map(el, { zoomControl: false, attributionControl: true, scrollWheelZoom: false });
            osmLayer(L).addTo(map);
            const w = [info.wlat, info.wlng];
            const me = [fix.lat, fix.lng];
            const r = info.radius || radius || 100;
            L.circle(w, { radius: r, className: 'att-geo-circle', weight: 2 }).addTo(map);
            L.marker(w, { icon: pinIcon(L), keyboard: false, interactive: false }).addTo(map);
            L.circle(me, { radius: fix.accuracy, className: 'att-geo-circle is-out', weight: 1 }).addTo(map);
            L.marker(me, { icon: pinIcon(L, 'is-me'), keyboard: false, interactive: false }).addTo(map);
            map.fitBounds(L.latLng(w).toBounds(r * 2).extend(me), { padding: [20, 20], maxZoom: 18 });
            sheetMap = map;
        }).catch(() => { el.hidden = true; });
    }

    // ── checking ──
    const apply = (resp) => {
        const wp = resp.workplace || {};
        info = {
            name: wp.name || '', radius: wp.radius || 0, wlat: wp.lat, wlng: wp.lng,
            distance: resp.distance, accuracy: resp.accuracy, method: resp.method, request: resp.request,
        };
        if (wp.radius) radius = wp.radius;
        return verdictState(resp);
    };

    // check runs one look (locateRun). quiet: no sheet on failure (the strip says it).
    // A press that joins a look still settling (the quiet first look) still gets its sheet: the flags merge
    // into the running look's options. A look that settled on a rough verdict and is still sharpening gives
    // way to a new check.
    const cancelRun = () => {
        if (!run) return;
        const r = run;
        run = null;
        r.stop();
        if (!r.settled) { r.settled = true; r.resolve(state); }
    };
    const check = (asked = {}) => {
        if (checking()) {
            const cur = run.opts;
            cur.openOnFail = cur.openOnFail || asked.openOnFail;
            cur.inSheet = cur.inSheet || asked.inSheet;
            cur.announce = cur.announce || asked.announce;
            if (asked.opener) cur.opener = asked.opener;
            return run.promise;
        }
        cancelRun();
        const opts = { ...asked };
        const prev = state;
        state = 'checking';
        slow = false;
        paint();
        if (opts.inSheet) sayInSheet(L10N.statusLocating || '', 'busy');
        const r = { settled: false, opts, stop: () => {} };
        r.promise = new Promise((res) => { r.resolve = res; });
        run = r;
        const slowTimer = setTimeout(() => { if (run === r && !r.settled) { slow = true; paint(); } }, SLOW_HINT_MS);
        let retried = false;
        const begin = () => {
            r.stop = locateRun(async (v, later) => {
                if (run !== r) return;
                if (later) { settleLater(v); return; }
                if (v.state === 'blocked') {
                    // Just allowed (a permission change, the site settings): the change event can arrive a moment
                    // before positions are let through, so a denial while the permission already reads 「허용」
                    // gets one more try.
                    const now = await permissionState();
                    if (run !== r) return;
                    if (now === 'granted' && !retried) {
                        retried = true;
                        setTimeout(() => { if (run === r) begin(); }, 400);
                        return;
                    }
                    // Chrome reports a dismissed prompt as a denial too — only the permission tells them apart.
                    perm = now;
                    if (now === 'prompt') v = { state: opts.fromAsk ? 'dismissed' : 'need' };
                }
                r.settled = true;
                clearTimeout(slowTimer);
                slow = false;
                settle(v, opts, prev);
                r.resolve(state);
            });
        };
        begin();
        return r.promise;
    };

    // settle shows a look's first answer: the strip, and — for a press or the sheet's own buttons — the
    // sheet page that explains a failure; a pass lets a waiting clock button go.
    const settle = (v, opts, prev) => {
        if (v.resp) apply(v.resp);
        if (v.fix) fix = v.fix;
        verdictAt = Date.now();
        const next = v.state;
        if (next === 'dismissed') {
            state = 'need';
            paint();
            sayInSheet(L10N.dismissed || '', '');
            return;
        }
        state = next;
        paint();
        if (next === 'ok') {
            // Take the waiting clock before closing the sheet — its onClose forgets it.
            const f = pendingForm;
            pendingForm = null;
            closeIfOpen();
            if (info.method === 'location' && (opts.inSheet || opts.announce) && !f) showToast(fill(L10N.verified, { name: info.name }));
            if (f) {
                if (info.method === 'request') { f.dataset.submitting = '1'; f.submit(); } else submitWithFix(f);
            }
            return;
        }
        if (next === 'failed') {
            if (opts.inSheet) sayInSheet(L10N.statusFailed || '', 'error');
            pendingForm = null;
            return;
        }
        const mode = sheetModeFor(next);
        if (mode && (opts.inSheet || opts.openOnFail)) {
            openMode(mode, next === 'timeout' ? 'timeout' : undefined, opts.opener);
            if (next === 'blocked' && prev === 'blocked' && opts.inSheet) sayInSheet(stillBlockedText(), 'error');
        } else if (!mode) {
            pendingForm = null;
        }
    };

    // settleLater: a sharper fix came after a rough verdict showed. The new verdict replaces it where it shows
    // (the strip, an open verdict sheet); a pass closes the sheet and lets a waiting clock button go.
    const VERDICT_SHEETS = new Set(['outside', 'inaccurate', 'approximate']);
    const settleLater = (v) => {
        apply(v.resp);
        fix = v.fix;
        verdictAt = Date.now();
        state = v.state;
        paint();
        const open = !!sheet && !sheet.hidden;
        if (state === 'ok') {
            const f = pendingForm;
            pendingForm = null;
            closeIfOpen();
            if (f) {
                if (info.method === 'request') { f.dataset.submitting = '1'; f.submit(); } else submitWithFix(f);
            } else if (open && info.method === 'location') {
                showToast(fill(L10N.verified, { name: info.name }));
            }
            return;
        }
        const mode = sheetModeFor(state);
        if (open && mode && VERDICT_SHEETS.has(sheet.dataset.mode)) showMode(mode);
    };

    // ── what a press does ──
    // startFlow is the one entry for the strip button and the clock buttons.
    const startFlow = async (opener) => {
        if (!window.isSecureContext) { state = 'insecure'; paint(); openMode('unsupported', 'insecure', opener); return; }
        if (!navigator.geolocation) { state = 'unsupported'; paint(); openMode('unsupported', 'unsupported', opener); return; }
        perm = await permissionState();
        if (perm === 'denied') { state = 'blocked'; paint(); openMode('blocked', undefined, opener); return; }
        if (perm === 'prompt') { openMode('ask', undefined, opener); return; }
        // granted, or unknown (the browser asks by itself if it has to)
        return check({ openOnFail: true, announce: true, opener });
    };

    stripBtn.addEventListener('click', () => {
        const act = stripBtn.dataset.act;
        if (act === 'help') { const mode = sheetModeFor(state); if (mode) openMode(mode, state === 'insecure' ? 'insecure' : state === 'timeout' ? 'timeout' : undefined, stripBtn); return; }
        if (act === 'howto') { openMode('blocked', undefined, stripBtn); return; }
        if (act === 'recheck' && state === 'ok') { check({ announce: true }); return; }
        startFlow(stripBtn);
    });

    sheet?.querySelector('[data-loc-ask]')?.addEventListener('click', () => {
        check({ inSheet: true, fromAsk: true });
    });
    sheet?.querySelector('[data-loc-recheck]')?.addEventListener('click', async () => {
        if (sheet.dataset.mode === 'blocked') {
            perm = await permissionState();
            if (perm === 'denied') { sayInSheet(stillBlockedText(), 'error'); return; }
        }
        check({ inSheet: true, fromAsk: perm === 'prompt' });
    });

    // The <geolocation> button: the browser shows its own prompt (with a way back from 「차단」) and tells us
    // how it went. Whatever it says, the permission is read back the usual way, so the page's own state and the
    // Permissions API stay the one truth (and its change event may already have started the check).
    if (regrant) {
        const afterPrompt = async () => {
            perm = await permissionState();
            if (perm === 'unknown' && geoEl.permissionStatus) perm = geoEl.permissionStatus;
            if (perm === 'granted') { check({ inSheet: !!(sheet && !sheet.hidden), announce: true }); return; }
            if (perm === 'denied') sayInSheet(L10N.regrantDenied || stillBlockedText(), 'error');
        };
        geoEl.addEventListener('promptaction', afterPrompt);
        geoEl.addEventListener('location', () => { if (!checking() && state !== 'ok') afterPrompt(); });
        geoEl.addEventListener('promptdismiss', () => sayInSheet(L10N.regrantDismissed || '', ''));
    }

    // ── clock buttons ──
    const setPending = (form, label) => {
        const btn = form.querySelector('button:not([type="button"])');
        if (!btn) return;
        if (!btn.dataset.idleLabel) btn.dataset.idleLabel = btn.textContent;
        btn.disabled = true;
        btn.classList.add('att-btn-pending');
        btn.textContent = label;
    };
    const unsetPending = (form) => {
        const btn = form.querySelector('button:not([type="button"])');
        if (!btn || form.dataset.submitting === '1') return;
        btn.disabled = false;
        btn.classList.remove('att-btn-pending');
        if (btn.dataset.idleLabel) btn.textContent = btn.dataset.idleLabel;
    };
    function submitWithFix(form) {
        if (!fix) return;
        form.querySelector('input[name="lat"]').value = String(fix.lat);
        form.querySelector('input[name="lng"]').value = String(fix.lng);
        const acc = form.querySelector('input[name="accuracy"]');
        if (acc) acc.value = String(Math.round(fix.accuracy * 10) / 10);
        form.dataset.submitting = '1';
        setPending(form, form.dataset.pendingLabel || '…');
        form.submit();
    }

    for (const form of forms) {
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            if (form.dataset.submitting === '1') return;
            const btn = form.querySelector('button:not([type="button"])');
            if (state === 'ok' && fresh(fix, Date.now(), FIX_MAX_AGE) && info.method !== 'request') {
                submitWithFix(form);
                return;
            }
            if (state === 'ok' && info.method === 'request') {
                // An approved remote / field day: the server lets it through without a position.
                form.dataset.submitting = '1';
                setPending(form, form.dataset.pendingLabel || '…');
                form.submit();
                return;
            }
            pendingForm = form;
            setPending(form, L10N.locating || '…');
            const release = () => { if (pendingForm !== form) unsetPending(form); };
            let ready;
            if (state === 'ok') {
                ready = check({ openOnFail: true, opener: btn });
            } else if (pressOpensSheet(state, verdictAt, Date.now())) {
                // The last look failed a moment ago: its explanation opens at once, and a fresh look runs behind
                // it (a pass closes the sheet and posts this button).
                openMode(sheetModeFor(state), state === 'timeout' ? 'timeout' : undefined, btn);
                ready = check({ inSheet: true, opener: btn });
            } else {
                ready = startFlow(btn);
            }
            Promise.resolve(ready).finally(() => {
                // A sheet may still be asking — keep the button busy until it closes or the check ends.
                if (sheet && !sheet.hidden && pendingForm === form) {
                    const watch = new MutationObserver(() => {
                        if (sheet.hidden) { watch.disconnect(); if (pendingForm === form) pendingForm = null; release(); }
                    });
                    watch.observe(sheet, { attributes: true, attributeFilter: ['hidden'] });
                    return;
                }
                if (pendingForm === form && !checking()) pendingForm = null;
                release();
            });
        });
    }

    // ── watch for changes ──
    const recheckOnReturn = async () => {
        if (document.visibilityState !== 'visible' || checking()) return;
        const before = perm;
        perm = await permissionState();
        if (perm === 'granted' && (state === 'blocked' || state === 'need' || before !== 'granted')) {
            check({ inSheet: !!(sheet && !sheet.hidden), announce: true });
        } else if (perm === 'denied' && state !== 'blocked') {
            state = 'blocked';
            cancelRun();
            paint();
            if (sheet && !sheet.hidden) showMode('blocked');
        } else if (state === 'ok' && !fresh(fix, Date.now(), 5 * 60000) && info.method === 'location') {
            check({});
        }
    };
    document.addEventListener('visibilitychange', recheckOnReturn);
    window.addEventListener('pageshow', (e) => { if (e.persisted) recheckOnReturn(); });
    if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' }).then((st) => {
            st.addEventListener('change', () => {
                perm = st.state;
                if (st.state === 'granted') check({ inSheet: !!(sheet && !sheet.hidden), announce: true });
                else if (st.state === 'denied') { state = 'blocked'; cancelRun(); paint(); if (sheet && !sheet.hidden) showMode('blocked'); }
                else if (st.state === 'prompt' && state === 'blocked') { state = 'need'; paint(); if (sheet && !sheet.hidden) showMode('ask'); }
            });
        }).catch(() => {});
    }

    // ── first look ──
    (async () => {
        if (!window.isSecureContext) { state = 'insecure'; paint(); return; }
        if (!navigator.geolocation) { state = 'unsupported'; paint(); return; }
        perm = await permissionState();
        if (perm === 'granted') { check({}); return; }
        state = perm === 'denied' ? 'blocked' : 'need';
        paint();
    })();

    strip.dataset.bound = '1';
    strip.dataset.regrant = regrant ? '1' : '0';
    return { check, state: () => state };
}
